import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { LatLng } from "use-places-autocomplete";
import { OnlinePaymentProvider } from "../../../utils/enums";
import {
  buildMenuLookupsFromCartLineInput,
  buildMenuLookupsFromCategories,
  buildMenuLookupsFromItems,
  mergeRecordMaps,
  mergeSubItemRecords,
} from "../../../utils/cart/menuLookup";
import { buildEntityImageLookup } from "../../../utils/LoyaltyUtils/loyaltyGraphicResolver";
import { EntityImageLookup } from "../../../utils/loyaltyTypes";
import {
  AddonGroupType,
  CategoryType,
  Franchise,
  FranchiseWithDistance,
  ItemDetailsType,
  ItemType,
  Restaurant,
  RestaurantConfig,
  ScheduledOrderDetailsType,
  SubItemType,
} from "../../../utils/types";
import {
  handleSelectPartnerRestaurant,
  handleSortActiveFranchises,
  setAllRestaurantDetails,
} from "../reduxHelpers/configReduxHelper";

export interface InitialState {
  error: string;
  restaurantConfig: RestaurantConfig | null;
  restaurantInfo: Restaurant | null;
  selectedFranchise?: Franchise;
  selectedLanguage: string;
  currency: string;
  partnerRestaurants: Restaurant[];
  activeFranchises: FranchiseWithDistance[];
  socialMedia: {
    fbId: string;
    googleTagsManager: string;
    fbDomainVerificationId: string;
    tiktokId: string;
    googleAnalytics: string;
    hasConvApiEnabled: boolean;
  };
  onlinePaymentProvider: OnlinePaymentProvider;
  showAppBanner: boolean;
  shouldClearCartDueToRestaurantSwitch: boolean;
  isRestaurantSwitchInProgress: boolean;
  entityImageLookup: EntityImageLookup;
  subItemLookupByUuid: Record<string, SubItemType>;
  /** Addon / nested sub-items indexed from ITEMS_DATA item detail pages. */
  addonSubItemLookupByUuid: Record<string, SubItemType>;
  /** Addon groups indexed by uuid from menu + item detail pages. */
  addonGroupLookupByUuid: Record<string, AddonGroupType>;
}

const initialState: InitialState = {
  error: "",
  restaurantInfo: null,
  partnerRestaurants: [],
  restaurantConfig: {
    qrcodeAutoSelect: false,
    settleBilleAndCompleteInOne: false,
  },

  selectedFranchise: undefined,
  activeFranchises: [],
  selectedLanguage: "en",
  currency: "$",
  socialMedia: {
    fbId: "",
    googleTagsManager: "",
    fbDomainVerificationId: "",
    tiktokId: "",
    googleAnalytics: "",
    hasConvApiEnabled: false,
  },
  showAppBanner: true,
  onlinePaymentProvider: OnlinePaymentProvider.Card,
  shouldClearCartDueToRestaurantSwitch: false,
  isRestaurantSwitchInProgress: false,
  entityImageLookup: {},
  subItemLookupByUuid: {},
  addonSubItemLookupByUuid: {},
  addonGroupLookupByUuid: {},
};

const config = createSlice({
  name: "config",
  initialState,
  reducers: {
    setAppBannerState: (state, action) => {
      state.showAppBanner = action.payload;
    },
    resetSelectedFranchise: (state) => {
      state.selectedFranchise = initialState.selectedFranchise;
    },
    setSelectedFranchise: (state, action) => {
      // Skip if a restaurant switch is already in progress to prevent race conditions
      if (state.isRestaurantSwitchInProgress) {
        console.log(
          "Skipping franchise selection - restaurant switch already in progress"
        );

        return;
      }

      // Validate franchise belongs to an available restaurant (current or partner)
      if (action.payload && state.restaurantInfo) {
        const allAvailableRestaurants = [
          state.restaurantInfo,
          ...state.partnerRestaurants,
        ];

        const franchiseRestaurant = allAvailableRestaurants.find(
          (restaurant) => restaurant.id === action.payload.restaurantId
        );

        // If franchise doesn't belong to any available restaurant, reject the selection
        if (!franchiseRestaurant) {
          console.warn(
            `Franchise with restaurantId ${action.payload.restaurantId} does not belong to any available restaurant. Ignoring franchise selection.`
          );

          return;
        }

        // If franchise belongs to a different restaurant than currently displayed, switch restaurant
        if (state.restaurantInfo.id !== action.payload.restaurantId) {
          handleSelectPartnerRestaurant(
            state,
            action.payload.restaurantId,
            state.restaurantInfo
          );
        }
      }

      state.selectedFranchise = action.payload;
    },

    setOnlinePaymentProvider: (state, action) => {
      state.onlinePaymentProvider = action.payload;
    },
    /**
     * Put the restaurant the page was built with into state.
     *
     * Deliberately NOT setAllRestaurantDetailsInRedux: that routes through
     * handleSettingAllRestaurantDetails, which reads an id differing from the
     * stored one as a restaurant SWITCH and raises
     * shouldClearCartDueToRestaurantSwitch. Hydrating a page is not a switch —
     * a guest reloading on a partner restaurant would have lost their cart, and
     * a stale persisted id could never heal, because the switch path falls back
     * to keeping whatever was already stored.
     *
     * The payload is authoritative: it comes from getStaticProps, which carries
     * the main restaurant and every partner. #4204
     */
    hydrateRestaurantFromProps: (state, action: PayloadAction<Restaurant>) => {
      const restaurant = action.payload;

      setAllRestaurantDetails(
        state,
        restaurant,
        restaurant.partnerRestaurants ?? []
      );

      // Self-heal: a franchise selection persisted from a previous deployment
      // of this domain may not exist here any more. setAllRestaurantDetails
      // re-points it when it resolves; this drops it when it does not, which is
      // what the request's 4xx retry path used to do.
      if (
        state.selectedFranchise &&
        !(restaurant.franchises ?? []).some(
          (franchise) => franchise.id === state.selectedFranchise?.id
        )
      ) {
        state.selectedFranchise = initialState.selectedFranchise;
      }
    },

    /**
     * Social media now arrives in the page props rather than from a thunk the
     * browser fired on every load. Merged into the initial shape rather than
     * replacing it, because a restaurant with no social-media row sends `{}`
     * and the consumers read the fields unguarded. #4166
     */
    setSocialMedia: (
      state,
      action: PayloadAction<Partial<InitialState["socialMedia"]> | null>
    ) => {
      const next = { ...initialState.socialMedia, ...action.payload };

      // Only write when the VALUE differs. Assigning unconditionally made every
      // dispatch look like a state change to every subscriber, which is what
      // let the render-phase dispatch in useRestaurantHydration spin (1,419
      // actions, 5-7s per navigation). Cheap: this object is a handful of ids.
      if (JSON.stringify(state.socialMedia) === JSON.stringify(next)) {
        return;
      }

      state.socialMedia = next;
    },

    setSelectedLanguageAndCurrency: (
      state,
      action: PayloadAction<{ currency: string; selectedLanguage: string }>
    ) => {
      state.selectedLanguage = action.payload.selectedLanguage;
      state.currency = action.payload.currency;
    },
    sortActiveFranchisesByDistance: (state, action: PayloadAction<LatLng>) => {
      handleSortActiveFranchises(state, action.payload);
    },
    setScheduledOrderFranchiseConfig: (
      state,
      { payload }: PayloadAction<Partial<ScheduledOrderDetailsType>>
    ) => {
      if (!state.selectedFranchise) {
        console.error("selectedFranchise is undefined in Redux state!");

        return;
      }

      if (!state.selectedFranchise.franchiseConfig) {
        state.selectedFranchise.franchiseConfig = {};
      }

      if (!state.selectedFranchise.franchiseConfig.scheduledOrder) {
        state.selectedFranchise.franchiseConfig.scheduledOrder = {};
      }

      state.selectedFranchise.franchiseConfig.scheduledOrder = {
        ...state.selectedFranchise.franchiseConfig.scheduledOrder,
        scheduledOrderNowCutoff: payload.scheduledOrderNowCutoff,
        scheduledOrderIftarCutoffMins: payload.scheduledOrderIftarCutoffMins,
      };
    },
    clearCartDueToRestaurantSwitchFlag: (state) => {
      state.shouldClearCartDueToRestaurantSwitch = false;
    },
    setRestaurantSwitchInProgress: (state, action: PayloadAction<boolean>) => {
      state.isRestaurantSwitchInProgress = action.payload;
    },
    setEntityImageLookup: (state, action: PayloadAction<CategoryType[]>) => {
      state.entityImageLookup = buildEntityImageLookup(action.payload);
      const menuLookups = buildMenuLookupsFromCategories(action.payload);

      state.subItemLookupByUuid = mergeSubItemRecords(
        menuLookups.subItemsByUuid,
        state.subItemLookupByUuid
      );
      state.addonSubItemLookupByUuid = mergeSubItemRecords(
        menuLookups.addonSubItemsByUuid,
        state.addonSubItemLookupByUuid
      );
      state.addonGroupLookupByUuid = mergeRecordMaps(
        menuLookups.addonGroupsByUuid,
        state.addonGroupLookupByUuid
      );
    },
    mergeMenuLookupsFromItems: (state, action: PayloadAction<ItemType[]>) => {
      const { subItemsByUuid, addonSubItemsByUuid, addonGroupsByUuid } =
        buildMenuLookupsFromItems(action.payload);

      state.subItemLookupByUuid = mergeSubItemRecords(
        state.subItemLookupByUuid,
        subItemsByUuid
      );
      state.addonSubItemLookupByUuid = mergeSubItemRecords(
        state.addonSubItemLookupByUuid,
        addonSubItemsByUuid
      );
      state.addonGroupLookupByUuid = mergeRecordMaps(
        state.addonGroupLookupByUuid,
        addonGroupsByUuid
      );
    },
    mergeMenuLookupsFromCartLineInput: (
      state,
      action: PayloadAction<{
        subItemDetails: SubItemType;
        itemDetails: ItemDetailsType;
        version?: number;
      }>
    ) => {
      const { subItemsByUuid, addonSubItemsByUuid, addonGroupsByUuid } =
        buildMenuLookupsFromCartLineInput(action.payload);

      state.subItemLookupByUuid = mergeSubItemRecords(
        state.subItemLookupByUuid,
        subItemsByUuid
      );
      state.addonSubItemLookupByUuid = mergeSubItemRecords(
        state.addonSubItemLookupByUuid,
        addonSubItemsByUuid
      );
      state.addonGroupLookupByUuid = mergeRecordMaps(
        state.addonGroupLookupByUuid,
        addonGroupsByUuid
      );
    },
  },
});

export const {
  setSelectedFranchise,
  resetSelectedFranchise,
  setSelectedLanguageAndCurrency,
  hydrateRestaurantFromProps,
  setSocialMedia,
  sortActiveFranchisesByDistance,
  setOnlinePaymentProvider,
  setAppBannerState,
  setScheduledOrderFranchiseConfig,
  clearCartDueToRestaurantSwitchFlag,
  setRestaurantSwitchInProgress,
  setEntityImageLookup,
  mergeMenuLookupsFromItems,
  mergeMenuLookupsFromCartLineInput,
} = config.actions;

export default config.reducer;
